import React, { useState, useEffect } from "react";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { useToast } from "../../../context/ToastContext";
import CreatableSelect from 'react-select/creatable';
import { useUser } from "../../../context/UserContext";
import { canAccessAdmin } from "../../../utils/roleUtils";
import { copyToClipboard } from "../../../utils/browserCompatibility";
import { sanitizeText, sanitizeEmail, sanitizePhone } from "../../../utils/sanitization";
import "../../../styles/modern-design-system.css";

// Cross-browser compatibility polyfills
const safeTrim = (str) => {
  if (typeof str !== 'string') return '';
  return str.trim ? str.trim() : str.replace(/^\s+|\s+$/g, '');
};

const safeToLowerCase = (str) => {
  if (typeof str !== 'string') return '';
  return str.toLowerCase ? str.toLowerCase() : str.replace(/[A-Z]/g, function(match) {
    return String.fromCharCode(match.charCodeAt(0) + 32);
  });
};

const CreateTripModal = ({ isOpen, onClose, onTripCreated, defaultDestination = "disney_world" }) => {
  const { user } = useUser();
  const { toast } = useToast();
  
  // Check if user has access to new destination features
  const hasNewDestinationAccess = () => {
    return user && canAccessAdmin(user.role);
  };
  const [step, setStep] = useState(1);
  const [tripName, setTripName] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [destination, setDestination] = useState(defaultDestination);
  const [questionnaireLink, setQuestionnaireLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [clientInput, setClientInput] = useState("");
  const [isExistingClient, setIsExistingClient] = useState(false);
  const [selectedClientOption, setSelectedClientOption] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetch(`${import.meta.env.VITE_API_URL}/api/clients`, {
        credentials: 'include',
      })
        .then((res) => res.json())
        .then((data) => {
          setClients(data);
        })
        .catch(() => setClients([]));
    }
  }, [isOpen]);

  // Prepare options for react-select with deduplication
  const clientOptions = clients
    .filter((client, index, self) => 
      index === self.findIndex(c => 
        c.clientid === client.clientid && (c.fullname || c.FullName) === (client.fullname || client.FullName)
      )
    )
    .map((client) => ({
      value: client.clientid,
      label: client.fullname || client.FullName || "Unknown",
      client,
    }));

  // In useEffect, when clients are loaded, if selectedClientId is set, update selectedClientOption accordingly
  useEffect(() => {
    if (selectedClientId) {
      const found = clientOptions.find(opt => opt.value === selectedClientId);
      setSelectedClientOption(found || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, selectedClientId]);

  const handleClientChange = (selectedOption) => {
    setSelectedClientOption(selectedOption);
    // Clear client name error when user selects/changes client
    if (errors.clientName) {
      setErrors(prev => ({ ...prev, clientName: undefined }));
    }
    
    if (!selectedOption) {
      setSelectedClientId("");
      setClientName(clientInput);
      setClientEmail("");
      setClientPhone("");
      setIsExistingClient(false);
      return;
    }
    if (selectedOption.client) {
      setSelectedClientId(selectedOption.value);
      setClientName(selectedOption.client.fullname || selectedOption.client.FullName || selectedOption.label || "");
      setClientEmail(selectedOption.client.email || selectedOption.client.Email || "");
      setClientPhone(selectedOption.client.phone || selectedOption.client.Phone || "");
      setIsExistingClient(true);
    } else {
      // New client
      setSelectedClientId("");
      setClientName(selectedOption.label || selectedOption.value || "");
      setClientEmail("");
      setClientPhone("");
      setIsExistingClient(false);
    }
  };

  const handleClientInputChange = (inputValue) => {
    setClientInput(inputValue);
    
    // Clear client name error when user types
    if (errors.clientName) {
      setErrors(prev => ({ ...prev, clientName: undefined }));
    }
    
    // Only reset if we don't have a valid selected client
    if (!selectedClientOption || !selectedClientOption.value) {
      setSelectedClientId("");
      setClientName(inputValue);
      setClientEmail("");
      setClientPhone("");
      setIsExistingClient(false);
    }
  };

  // Phone number formatting for (###) ###-####
  const formatPhoneNumber = (value) => {
    if (value == null || typeof value !== 'string') return '';
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length >= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
    if (phoneNumber.length >= 3) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    if (phoneNumber.length > 0) {
      return `(${phoneNumber}`;
    }
    return '';
  };

  if (!isOpen) return null;

  // Validation functions - cross-browser compatible
  const validateEmail = (email) => {
    if (!email || !safeTrim(email)) return true; // Email is optional
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    if (!phone || !safeTrim(phone)) return true; // Phone is optional
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{7,20}$/;
    return phoneRegex.test(phone);
  };

  const validateName = (name) => {
    if (!name || typeof name !== 'string') return false;
    const trimmedName = safeTrim(name);
    if (trimmedName.length < 2) return false;
    if (trimmedName.length > 100) return false;
    const nameRegex = /^[a-zA-Z\s\-']+$/;
    return nameRegex.test(trimmedName);
  };

  // Trip name validation - just check length since it's optional
  // Special characters are allowed and will be sanitized on submit

  const validateForm = () => {
    const newErrors = {};

    // Validate trip name (optional) - just check length, allow most characters
    if (tripName && safeTrim(tripName).length > 100) {
      newErrors.tripName = "Trip name must be 100 characters or less.";
    }

    // Validate client: when existing client is selected, name is not required; otherwise required
    if (selectedClientId && isExistingClient) {
      // Existing client selected – no client name validation needed
    } else if (!clientName || !safeTrim(clientName)) {
      newErrors.clientName = "Client name is required.";
    } else if (!validateName(clientName)) {
      newErrors.clientName = "Please enter a valid client name (2-100 characters, letters only).";
    }

    // Validate client email (optional)
    if (clientEmail && safeTrim(clientEmail) && !validateEmail(clientEmail)) {
      newErrors.clientEmail = "Please enter a valid email address.";
    }

    // Validate client phone (optional)
    if (clientPhone && safeTrim(clientPhone) && !validatePhone(clientPhone)) {
      newErrors.clientPhone = "Please enter a valid phone number (e.g., (123) 456-7890 or 123-456-7890).";
    }

    setErrors(newErrors);
    // Cross-browser compatible object key counting
    let keyCount = 0;
    for (let key in newErrors) {
      if (newErrors.hasOwnProperty(key)) {
        keyCount++;
      }
    }
    return keyCount === 0;
  };

  const handleCreateTrip = async () => {
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    setErrors({}); // Clear any previous errors
    
    try {
      // Cross-browser compatible API call
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      
      // Build payload based on whether existing client is selected
      const payload = {
        tripName: safeTrim(sanitizeText(tripName)) || null,
        destination: destination,
      };
      
      // If existing client is selected, only send clientId (NEVER create duplicate)
      if (selectedClientId && isExistingClient) {
        payload.clientId = selectedClientId;
      } else {
        // New client - send client details
        payload.clientName = safeTrim(sanitizeText(clientName));
        payload.clientEmail = safeToLowerCase(safeTrim(sanitizeEmail(clientEmail))) || null;
        payload.phone = safeTrim(sanitizePhone(clientPhone)) || null;
      }
      
      const res = await fetch(`${apiUrl}/api/trips`, {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      // Cross-browser compatible JSON parsing
      let data;
      if (res.json) {
        data = await res.json();
      } else {
        // Fallback for very old browsers
        const text = await res.text();
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          throw new Error("Invalid response format");
        }
      }
      
      if (!res.ok) {
        // Handle specific error cases - cross-browser compatible
        if (res.status === 409 || 
            (data.error && (data.error.includes('already exists') || data.error.includes('duplicate')))) {
          setErrors({ clientName: "A client with this name already exists." });
          toast.error("A client with this name already exists.");
          return;
        }
        throw new Error(data.message || data.error || "Failed to create trip");
      }
      // Create simplified questionnaire link
      const baseUrl = window.location.origin;
      const questionnaireLink = `${baseUrl}/questionnaire/${data.tripId}`;
      setQuestionnaireLink(questionnaireLink);
      setStep(2);
      onTripCreated({
        id: data.tripId,
        tripname: safeTrim(tripName) || `${safeTrim(clientName)}'s Disney Trip`,
        clientName: safeTrim(clientName),
        status: "Pending",
        createdAt: data.createdat || new Date().toISOString(),
      });
    } catch (err) {
      console.error("Error creating trip:", err);
      // Cross-browser compatible error handling
      if (err.message && (err.message.includes('already exists') || err.message.includes('duplicate'))) {
        setErrors({ clientName: "A client with this name already exists." });
      }
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      // Reset form and errors
      setTripName("");
      setClientName("");
      setClientEmail("");
      setClientPhone("");
      setDestination(defaultDestination);
      setQuestionnaireLink("");
      setSelectedClientId("");
      setClientInput("");
      setIsExistingClient(false);
      setSelectedClientOption(null);
      setErrors({});
      setStep(1);
    }
  };

  const handleCopy = async (link) => {
    try {
      const success = await copyToClipboard(link);
      if (success) {
        toast.success("Questionnaire link copied to clipboard!");
      } else {
        toast.error("Failed to copy. Please copy it manually.");
      }
    } catch (error) {
      console.warn('Copy failed:', error);
      toast.error("Failed to copy. Please copy it manually.");
    }
  };

  const handleEmail = (link) => {
    if (!clientEmail) return;
    const agentName = (user?.firstname || user?.lastname)
      ? `${user?.firstname || ""} ${user?.lastname || ""}`.trim()
      : `Your ${user?.agencyName || user?.agency_name || "ParkPro Agent"}`;
    const subject = encodeURIComponent("Your ParkPro Trip Questionnaire");
    const body = encodeURIComponent(
      `Hi ${clientName || ""},\n\nPlease fill out your trip questionnaire using the link below:\n\n${link}\n\nLet me know if you have any questions!\n\n– ${agentName}`
    );
    window.location.href = `mailto:${clientEmail}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center px-4 bg-black/40 sm:px-6">
      <div className="p-5 w-full max-w-2xl bg-white rounded-2xl border border-gray-200 shadow-2xl animate-fade-in-up max-h-[90vh] overflow-y-auto">
        {step === 1 && (
          <>
            <div className="flex justify-between items-center mb-3">
              <div>
                <h2 className="mb-1 text-xl font-bold text-gray-900">
                  Create a New Trip
                </h2>
                <p className="text-xs text-gray-500">
                  Enter your client's details to generate their trip questionnaire
                </p>
              </div>
            </div>

            <form className="space-y-2.5">
              <div>
                <label htmlFor="destination" className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                  Destination <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    id="destination"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    className="px-4 py-3 pr-10 w-full font-medium text-white bg-gray-700 rounded-lg border-2 border-gray-600 transition-all duration-200 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500"
                  >
                    <option value="disney_world">Walt Disney World Resort</option>
                    {hasNewDestinationAccess() && (
                      <>
                        <option value="disneyland">Disneyland Resort California</option>
                        <option value="universal_orlando">Universal Orlando Resort</option>
                        <option value="disney_cruise">Disney Cruise Line</option>
                      </>
                    )}
                  </select>
                  {/* Chevron icon */}
                  <svg
                    className="absolute right-3 top-1/2 w-5 h-5 text-gray-300 -translate-y-1/2 pointer-events-none"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.17l3.71-2.94a.75.75 0 111.04 1.08l-4.24 3.36a.75.75 0 01-.94 0L5.21 8.31a.75.75 0 01.02-1.1z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>

              <div>
                <label htmlFor="tripName" className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                  Trip Name (Optional)
                  <span className="relative ml-1 text-gray-400 cursor-help group">
                    <span className="inline-block w-4 h-4 text-xs leading-4 text-center bg-gray-200 rounded-full">?</span>
                    <span className="absolute bottom-full left-1/2 z-10 px-3 py-2 mb-2 w-64 text-xs text-white bg-gray-900 rounded-lg opacity-0 transition-opacity duration-200 transform -translate-x-1/2 pointer-events-none group-hover:opacity-100">
                      This is the name that will appear on your client's itinerary. If left blank, it will use the client's name followed by "Disney Trip" (e.g., "John Smith Disney Trip").
                      <span className="absolute top-full left-1/2 w-0 h-0 border-t-4 border-r-4 border-l-4 border-transparent transform -translate-x-1/2 border-t-gray-900"></span>
                    </span>
                  </span>
                </label>
                <div className="relative">
                  <input
                    id="tripName"
                    type="text"
                    value={tripName}
                    onChange={(e) => {
                      // Store raw input value - don't sanitize during typing
                      setTripName(e.target.value);
                      if (errors.tripName) {
                        setErrors(prev => ({ ...prev, tripName: undefined }));
                      }
                    }}
                    placeholder="Enter trip name"
                    maxLength={100}
                    className={`block w-full p-3 text-sm font-medium bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 placeholder-gray-300 appearance-none ${
                      errors.tripName ? 'border-red-500 focus:ring-red-500' : 
                      tripName ? 'border-blue-300 focus:ring-blue-500' : ''
                    }`}
                    style={{
                      fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      height: '44px'
                    }}
                    aria-describedby={`${errors.tripName ? 'tripName-help' : ''}`}
                    aria-invalid={errors.tripName ? 'true' : 'false'}
                    autoComplete="off"
                  />
                  
                  {/* Validation Icons */}
                  {errors.tripName && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    </div>
                  )}
                  {tripName && !errors.tripName && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <CheckCircle className="w-5 h-5 text-blue-500" />
                    </div>
                  )}
                </div>
                
                {errors.tripName && (
                  <p id="tripName-help" className="flex items-center space-x-1 text-sm text-red-400" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.tripName}</span>
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="clientSelect" className="block mb-2 text-sm font-medium text-gray-900">
                  Client Name *
                </label>
                <CreatableSelect
                  id="clientSelect"
                  options={clientOptions}
                  value={selectedClientOption}
                  onChange={handleClientChange}
                  onInputChange={handleClientInputChange}
                  inputValue={clientInput}
                  placeholder="Select or create new client"
                  isClearable
                  noOptionsMessage={() => clientInput ? 'No matching client. Enter a new name.' : 'Type to search clients...'}
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({ 
                      ...base, 
                      minHeight: 48,
                      maxHeight: 48,
                      height: 48,
                      fontSize: 14,
                      fontWeight: 500,
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      backgroundColor: '#374151',
                      border: '2px solid #d1d5db',
                      borderRadius: '8px',
                      padding: '0 16px',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#ffffff',
                      '&:hover': {
                        borderColor: '#9ca3af'
                      },
                      '&:focus-within': {
                        borderColor: '#3b82f6',
                        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.12)'
                      }
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '0',
                      margin: '0',
                      display: 'flex',
                      alignItems: 'center',
                      height: '100%'
                    }),
                    input: (base) => ({
                      ...base,
                      fontSize: 14,
                      fontWeight: 500,
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      color: '#ffffff', // text-white to match other fields
                      margin: '0',
                      padding: '0'
                    }),
                    placeholder: (base) => ({
                      ...base,
                      fontSize: 14,
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      color: '#ffffff',
                      margin: '0',
                      padding: '0'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      fontSize: 14,
                      fontWeight: 500,
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      color: '#ffffff',
                      margin: '0',
                      padding: '0'
                    }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                    option: (base, state) => ({
                      ...base,
                      fontSize: 14,
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      backgroundColor: state.isFocused ? '#3b82f6' : '#ffffff',
                      color: state.isFocused ? 'white' : '#111827',
                      '&:hover': {
                        backgroundColor: '#3b82f6',
                        color: 'white'
                      }
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center'
                    }),
                    indicatorSeparator: (base) => ({
                      ...base,
                      display: 'none'
                    })
                  }}
                />
                {errors.clientName && (
                  <p className="mt-1 text-sm text-red-600">{errors.clientName}</p>
                )}
              </div>

              <div>
                <label htmlFor="clientEmail" className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                  Client Email (Optional)
                </label>
                <div className="relative">
                  <input
                    id="clientEmail"
                    type="email"
                    value={clientEmail}
                    onChange={(e) => {
                      const lowercasedEmail = sanitizeEmail(e.target.value.toLowerCase());
                      setClientEmail(lowercasedEmail);
                      if (errors.clientEmail) {
                        setErrors(prev => ({ ...prev, clientEmail: undefined }));
                      }
                    }}
                    disabled={isExistingClient}
                    placeholder="Enter client's email address"
                    className={`block w-full p-3 text-sm font-medium bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 placeholder-gray-300 appearance-none disabled:bg-gray-600 disabled:text-gray-400 disabled:border-gray-500 ${
                      errors.clientEmail ? 'border-red-500 focus:ring-red-500' : 
                      clientEmail ? 'border-blue-300 focus:ring-blue-500' : ''
                    }`}
                    style={{
                      fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      height: '44px'
                    }}
                    aria-describedby={`${errors.clientEmail ? 'clientEmail-help' : ''}`}
                    aria-invalid={errors.clientEmail ? 'true' : 'false'}
                    autoComplete="off"
                  />
                  
                  {/* Validation Icons */}
                  {errors.clientEmail && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    </div>
                  )}
                  {clientEmail && !errors.clientEmail && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <CheckCircle className="w-5 h-5 text-blue-500" />
                    </div>
                  )}
                </div>
                
                {errors.clientEmail && (
                  <p id="clientEmail-help" className="flex items-center space-x-1 text-sm text-red-400" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.clientEmail}</span>
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="clientPhone" className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                  Client Phone (Optional)
                </label>
                <div className="relative">
                  <input
                    id="clientPhone"
                    type="tel"
                    value={clientPhone}
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      setClientPhone(formatted);
                      if (errors.clientPhone) {
                        setErrors(prev => ({ ...prev, clientPhone: undefined }));
                      }
                    }}
                    disabled={isExistingClient}
                    placeholder="(###) ###-####"
                    maxLength={14}
                    className={`block w-full p-3 text-sm font-medium bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 placeholder-gray-300 appearance-none disabled:bg-gray-600 disabled:text-gray-400 disabled:border-gray-500 ${
                      errors.clientPhone ? 'border-red-500 focus:ring-red-500' : 
                      clientPhone ? 'border-blue-300 focus:ring-blue-500' : ''
                    }`}
                    style={{
                      fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      height: '44px'
                    }}
                    aria-describedby={`${errors.clientPhone ? 'clientPhone-help' : ''}`}
                    aria-invalid={errors.clientPhone ? 'true' : 'false'}
                    autoComplete="off"
                  />
                  
                  {/* Validation Icons */}
                  {errors.clientPhone && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    </div>
                  )}
                  {clientPhone && !errors.clientPhone && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <CheckCircle className="w-5 h-5 text-blue-500" />
                    </div>
                  )}
                </div>
                
                {errors.clientPhone && (
                  <p id="clientPhone-help" className="flex items-center space-x-1 text-sm text-red-400" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.clientPhone}</span>
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateTrip}
                  disabled={loading}
                  className="flex-1 btn btn-magic"
                >
                  {loading ? (
                    <div className="flex justify-center items-center">
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      Creating...
                    </div>
                  ) : (
                    "Create Trip"
                  )}
                </button>
              </div>
            </form>
          </>
        )}

        {step === 2 && (
          <div className="text-center">
            <h2 className="mb-2 text-2xl font-bold text-gray-900">
              Trip Created!
            </h2>
            <p className="mb-5 text-sm text-gray-600">
              Share the questionnaire link below with your client to collect their trip preferences.
            </p>

            {/* Questionnaire Link */}
            <div className="p-4 mb-6 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="mb-3 text-lg font-semibold text-blue-900">Questionnaire Link ✨</h3>
              <p className="mb-3 text-sm text-blue-700">Share this link with your client to collect their trip preferences</p>
              
              <div className="flex gap-2 justify-center items-stretch mb-3 w-full">
                <input
                  type="text"
                  readOnly
                  value={questionnaireLink}
                  className="flex-1 px-4 py-3 font-mono text-sm text-gray-700 bg-white rounded-lg border-2 border-gray-300 focus:outline-none"
                />
                <button
                  onClick={() => handleCopy(questionnaireLink)}
                  className="self-center btn btn-primary"
                  title="Copy questionnaire link"
                  aria-label="Copy questionnaire link to clipboard"
                >
                  Copy
                </button>
              </div>

              {clientEmail && (
                <button
                  onClick={() => handleEmail(questionnaireLink)}
                  className="btn btn-secondary"
                >
                  Email Questionnaire Link
                </button>
              )}
            </div>

            <div className="flex gap-4 justify-center">
              <button
                onClick={handleClose}
                className="btn btn-magic"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateTripModal;